import { motion } from "framer-motion";
import { MapPin, Clock, RefreshCw, Ticket, Sparkles, TreePine, CloudRain } from "lucide-react";
import { CategoryIcon } from "./CategoryIcon";
import { Button } from "./ui/button";

const STEP_COLORS = ["neon-magenta", "neon-cyan", "neon-amber"];
const OUTDOOR_WORDS = ["amphitheat", "park", "pavilion", "outdoor", "lawn", "garden", "patio", "rooftop", "terrace", "field", "stadium", "plaza", "beach", "pier"];

export function isOutdoor(stop) {
  if (typeof stop.outdoor === "boolean") return stop.outdoor;
  const n = (stop.name || "").toLowerCase();
  return OUTDOOR_WORDS.some((w) => n.includes(w));
}

function StopMeta({ stop, index, outdoor }) {
  return (
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
      <Clock size={13} />
      <span data-testid={`stop-time-${index}`}>{stop.time_label}</span>
      <span className="mx-1 h-1 w-1 rounded-full bg-muted-foreground" />
      <span>{stop.category_label}</span>
      {outdoor && (
        <span data-testid={`outdoor-tag-${index}`} className="ml-1 inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] normal-case tracking-normal text-foreground">
          <TreePine size={11} className="text-emerald-400" /> Outdoor
        </span>
      )}
    </div>
  );
}

function RainNotice({ stop, index }) {
  return (
    <div data-testid={`rain-flag-${index}`} className="mt-4 flex items-start gap-2 rounded-xl border border-sky-400/40 bg-sky-500/10 p-3 text-sm">
      <CloudRain size={15} className="mt-0.5 shrink-0 text-sky-400" />
      <p className="text-foreground">
        <span className="font-semibold">Rain likely tonight</span> and this looks like an outdoor spot —
        {stop.swappable ? " consider swapping or bring an umbrella." : " check for a rain policy and pack a jacket."}
      </p>
    </div>
  );
}

function StopActions({ stop, index, onSwap, swapping }) {
  return (
    <div className="mt-4 flex flex-wrap gap-3">
      {stop.type === "event" && stop.ticket_url && (
        <a href={stop.ticket_url} target="_blank" rel="noopener noreferrer">
          <Button data-testid={`ticket-link-${index}`} size="sm" className="rounded-full cta-gradient text-white font-semibold hover:opacity-90">
            <Ticket size={15} className="mr-1.5" /> Get Tickets
          </Button>
        </a>
      )}
      {stop.swappable && (
        <Button
          data-testid={`swap-stop-button-${index}`}
          size="sm"
          variant="outline"
          disabled={swapping}
          onClick={() => onSwap(index)}
          className="rounded-full font-semibold"
        >
          <RefreshCw size={15} className={`mr-1.5 ${swapping ? "animate-spin" : ""}`} />
          {swapping ? "Finding…" : "Swap venue"}
        </Button>
      )}
    </div>
  );
}

export function StopCard({ stop, index, active, onHover, onSwap, swapping, rainLikely = false }) {
  const outdoor = isOutdoor(stop);
  return (
    <motion.div
      data-testid={`stop-card-${index}`}
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => onHover(null)}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.12 }}
      className={`relative rounded-2xl border bg-card p-6 sm:p-7 transition-shadow ${
        active ? "border-transparent ring-2 ring-accent glow" : "border-border"
      }`}
    >
      <div className="flex items-start gap-5">
        <div className="flex flex-col items-center gap-2 shrink-0">
          <span className={`flex h-11 w-11 items-center justify-center rounded-full bg-${STEP_COLORS[index % 3]} font-display text-lg font-extrabold text-white`}>
            {index + 1}
          </span>
          <span className="w-px flex-1 bg-border" />
        </div>

        <div className="flex-1 min-w-0">
          <StopMeta stop={stop} index={index} outdoor={outdoor} />

          <div className="mt-2 flex items-start gap-3">
            <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary text-foreground">
              <CategoryIcon name={stop.icon} size={20} />
            </span>
            <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight break-words">{stop.name}</h3>
          </div>

          {stop.type === "event" && stop.image && (
            <div className="mt-4 overflow-hidden rounded-xl border border-border">
              <img src={stop.image} alt={stop.name} className="h-44 w-full object-cover" loading="lazy" />
            </div>
          )}

          <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
            <MapPin size={15} className="mt-0.5 shrink-0" />
            <span className="break-words">{stop.address || "Address unavailable"}</span>
          </p>

          {outdoor && rainLikely && <RainNotice stop={stop} index={index} />}

          {stop.reason && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-border bg-secondary/50 p-3">
              <Sparkles size={15} className="mt-0.5 shrink-0 neon-magenta" />
              <p className="text-sm italic leading-relaxed text-foreground" data-testid={`stop-reason-${index}`}>{stop.reason}</p>
            </div>
          )}

          <StopActions stop={stop} index={index} onSwap={onSwap} swapping={swapping} />
        </div>
      </div>
    </motion.div>
  );
}
